import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GlobalSearch } from "@/components/GlobalSearch";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, Hammer, KanbanSquare, Users, CreditCard, LineChart,
  FileText, Wrench, Images, Bell, LogOut, Plus, Menu, X, ChevronRight, KeyRound,
} from "lucide-react";

function BrandMark({ small }) {
  return (
    <div className="leading-none">
      <Logo className={small ? "h-6" : "h-8"} />
      {!small && <div className="eyebrow text-[0.5rem] mt-1.5 pl-0.5">Bonded by Bullion</div>}
    </div>
  );
}

const ICONS = { LayoutDashboard, Hammer, KanbanSquare, Users, CreditCard, LineChart, FileText, Wrench, Images, Bell };

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [mobileNav, setMobileNav] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setUnread(data.unread);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const adminNav = [
    ["Dashboard", "/", "LayoutDashboard"],
    ["Trade Board", "/board", "KanbanSquare"],
    ["Trade Jobs", "/jobs", "Hammer"],
    ["Members", "/members", "Users"],
    ["Payments", "/payments", "CreditCard"],
    ["Financials", "/financials", "LineChart"],
    ["Documents", "/documents", "FileText"],
    ["Workshop", "/workshop", "Wrench"],
    ["Content Hub", "/content", "Images"],
    ["Notifications", "/notifications", "Bell"],
  ];
  const memberNav = [
    ["Dashboard", "/", "LayoutDashboard"],
    ["My Jobs", "/jobs", "Hammer"],
    ["Membership", "/membership", "CreditCard"],
    ["Documents", "/documents", "FileText"],
    ["Workshop", "/workshop", "Wrench"],
    ["Content Hub", "/content", "Images"],
    ["Notifications", "/notifications", "Bell"],
  ];
  const nav = isAdmin ? adminNav : memberNav;

  const NavItems = ({ onClick }) => (
    <nav className="flex flex-col gap-0.5">
      {nav.map(([label, path, icon]) => {
        const Icon = ICONS[icon];
        return (
          <NavLink key={path} to={path} end={path === "/"} onClick={onClick}
            data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all ${
                isActive ? "bg-[#1A1A1E] text-[#F5F3EF]" : "text-[#8C867A] hover:text-[#E2DFD7] hover:bg-[#131316]"
              }`}>
            {({ isActive }) => (
              <>
                <span className="w-0.5 h-4 rounded-full transition-all" style={{ backgroundColor: isActive ? "#D4AF37" : "transparent" }} />
                <Icon size={17} className={isActive ? "brass-text" : ""} />
                <span className="flex-1">{label}</span>
                {label === "Notifications" && unread > 0 && (
                  <span className="text-[0.6rem] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#D4AF37", color: "#0D0D0E" }}>{unread}</span>
                )}
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen grain flex bg-[#0D0D0E]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r hairline bg-[#0D0D0E] fixed inset-y-0 z-30">
        <div className="px-5 py-5 border-b hairline"><BrandMark /></div>
        <div className="flex-1 overflow-y-auto px-3 py-4"><NavItems /></div>
        <div className="px-3 py-3 border-t hairline">
          <div className="px-3 py-2 mb-1">
            <div className="text-sm text-[#E2DFD7] truncate">{user?.name}</div>
            <div className="eyebrow text-[0.55rem]">{isAdmin ? "Administrator" : "Bench Holder"}</div>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-[#8C867A] hover:text-[#E2DFD7] hover:bg-[#131316] transition-all">
            <LogOut size={17} /> Sign Out
          </button>
          <NavLink to="/change-password" data-testid="nav-change-password" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-[#8C867A] hover:text-[#E2DFD7] hover:bg-[#131316] transition-all">
            <KeyRound size={16} /> Change Password
          </NavLink>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileNav(false)} />
          <aside className="relative w-72 max-w-[80%] bg-[#0D0D0E] border-r hairline flex flex-col">
            <div className="px-5 py-5 border-b hairline flex items-center justify-between">
              <BrandMark />
              <X size={20} className="text-[#8C867A]" onClick={() => setMobileNav(false)} />
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-4"><NavItems onClick={() => setMobileNav(false)} /></div>
            <div className="px-3 py-3 border-t hairline">
              <button onClick={() => { logout(); navigate("/login"); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-[#8C867A]">
                <LogOut size={17} /> Sign Out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 border-b hairline bg-[#0D0D0E]/90 backdrop-blur-xl">
          <div className="flex items-center gap-3 px-4 lg:px-8 py-3">
            <button className="lg:hidden text-[#C2BEB5]" onClick={() => setMobileNav(true)} data-testid="mobile-menu-btn"><Menu size={22} /></button>
            <div className="lg:hidden"><BrandMark small /></div>
            <div className="flex-1 hidden sm:block"><GlobalSearch isAdmin={isAdmin} /></div>
            <NavLink to="/notifications" className="relative p-2 text-[#C2BEB5] hover:brass-text transition-colors" data-testid="topbar-notifications">
              <Bell size={19} />
              {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: "#D4AF37" }} />}
            </NavLink>
            {!isAdmin && (
              <button data-testid="topbar-new-job" onClick={() => navigate("/jobs/new")}
                className="hidden sm:flex btn-brass items-center gap-1.5 px-3.5 py-2 rounded-sm text-sm">
                <Plus size={15} /> New Job
              </button>
            )}
          </div>
          <div className="sm:hidden px-4 pb-3"><GlobalSearch isAdmin={isAdmin} /></div>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-6 pb-24 lg:pb-8 relative z-10">{children}</main>

        {/* Mobile bottom quick bar (bench holder) */}
        {!isAdmin && (
          <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t hairline bg-[#0D0D0E]/95 backdrop-blur-xl">
            <div className="grid grid-cols-4 gap-1 px-2 py-2">
              {[["/", LayoutDashboard, "Home"], ["/jobs", Hammer, "Jobs"], ["/content", Images, "Content"], ["/workshop", Wrench, "Workshop"]].map(([p, I, l]) => (
                <NavLink key={p} to={p} end={p === "/"} className={({ isActive }) =>
                  `flex flex-col items-center gap-1 py-1.5 rounded-sm text-[0.6rem] ${isActive ? "brass-text" : "text-[#8C867A]"}`}>
                  <I size={19} /> {l}
                </NavLink>
              ))}
            </div>
            <button onClick={() => navigate("/jobs/new")} data-testid="mobile-new-job"
              className="btn-brass absolute -top-6 right-4 w-12 h-12 rounded-full flex items-center justify-center shadow-lg">
              <Plus size={22} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PageHeader({ eyebrow, title, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-7 rise">
      <div>
        {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5F3EF]">{title}</h1>
      </div>
      {children}
    </div>
  );
}
